import TimeUtils from 'app/common/util/TimeUtils';
import Utils from 'app/common/util/Utils';
import {
    CdmStatus,
    CfmuFlightType,
    CtotLimitReasonType,
    DatasetType,
    EnrichedFlightId,
    ExclusionFromRegulationsDto,
    FLIGHT_ATFCM_MCDM_ONLY_LOC_CLAZZ_VALUE,
    FLIGHT_ATFCM_REGULATION_LOC_CLAZZ_VALUE,
    FLIGHT_ATFCM_REROUTING_LOC_CLAZZ_VALUE,
    FlightAirspaceCrossing,
    FlightAtfcmMeasureLoc,
    FlightCdmInfo,
    FlightCriticalityIndicatorDto,
    FlightCriticalityKind,
    FlightDto,
    FlightFieldType,
    FlightId,
    FlightKeys,
    FlightModelType,
    FlightPointDto,
    FlightRegLocation,
    InnoveFlightFieldType,
    IntruderKindType,
    LatLonPoint,
    LoadClassification,
    McdmState,
    OtmvAlert_AlertStatus,
    ProfileValidityDto,
    ProfileValidityDto_ProfileValidityKind,
    ReroutableStatusType,
    ReroutingIndicator,
    ReroutingReason,
    ReroutingRouteIdDto,
    ReroutingRouteType,
    RoutePoint,
    StandardFlightFieldType,
    StandardNppFlightFieldType,
    SuspensionStatus,
    TargetTimeDto,
    TimeAndTrafficType,
    TrafficType,
    VerticalTrendType,
} from 'app/model/CORE';
import KeyUtils from 'app/common/util/KeyUtils';
import AppContext from 'app/common/context/AppContext';
import { ConvectionSource } from 'app/model/SOL49';

export default class FlightUtils {
    static readonly NM_PROPOSAL_CONTEXT = 'NM';
    static readonly NM_PROPOSAL_CONTEXT_LIST = [FlightUtils.NM_PROPOSAL_CONTEXT];

    /**
     * Object that contains functions that map FlightFieldTypes to DTO property field names
     * Setup to handle CORE FlightFieldTypes.
     * Applicaitons may add their own mappers if needed
     */
    static PROPERTY_MAPPER = {
        'eurocontrol.npp.core.shared.dto.flight.StandardFlightFieldType': FlightUtils.toStandardFlightFieldPropertyName,
        'eurocontrol.npp.core.shared.dto.flight.InnoveFlightFieldType': FlightUtils.toInnoveFlightFieldPropertyName,
        'eurocontrol.npp.core.shared.dto.flight.StandardNppFlightFieldType':
            FlightUtils.toStandardNppFlightFieldPropertyName,
    };

    /**
     * Shallow copy the given fields from source to target
     * The method will refer to mapper fns defined in the static variable PROPERTY_MAPPER.
     * Applicaitons may add their own mappers if needed to PROPERTY_MAPPER.
     * If not mapper fn is defined, then this function will attempt to copy fields based on UpperCase converted to camelCase (e.g. "SOME_FIELD" to "someField").
     * @param target
     * @param source
     * @param fields
     */
    static shallowCopy(target: FlightDto, source: FlightDto, fields: FlightFieldType[]) {
        target.flightId = source.flightId;
        target.dataset = source.dataset;
        for (const field of fields) {
            const mapperFn = FlightUtils.PROPERTY_MAPPER[field[0]] || FlightUtils.defaultFlightFieldPropertyName;
            const prop = mapperFn(field);
            if (prop) {
                target[prop] = source[prop];
            }
        }
    }

    static isSameFlight(fid1: FlightId, fid2: FlightId): boolean {
        if (fid1 === fid2) return true;
        if (!fid1 || !fid2) return false;
        return !fid1.id || !fid2.id ? Utils.isEquals(fid1.flightKeys, fid2.flightKeys) : fid1.id === fid2.id;
    }

    static isPartialFlightIdMatch(fid1: FlightId, fid2: FlightId): boolean {
        if (fid1 === fid2) return true;
        if (!fid1 || !fid2) return false;
        return fid1.id && fid2.id
            ? fid1.id === fid2.id
            : FlightUtils.isPartialKeysMatch(fid1.flightKeys, fid2.flightKeys);
    }

    static isPartialKeysMatch(fullFlightKeys: FlightKeys, partialFlightKeys: FlightKeys): boolean {
        if (!partialFlightKeys || !fullFlightKeys) return false;
        if (fullFlightKeys === partialFlightKeys) return true;

        if (partialFlightKeys.eobt) {
            if (partialFlightKeys.eobt.getTime() !== fullFlightKeys.eobt.getTime()) return false;
        }

        if (partialFlightKeys.callsign) {
            if (partialFlightKeys.callsign !== fullFlightKeys.callsign) return false;
        }
        if (partialFlightKeys.adep) {
            if (partialFlightKeys.adep !== fullFlightKeys.adep) return false;
        }

        if (partialFlightKeys.ades) {
            if (partialFlightKeys.ades !== fullFlightKeys.ades) return false;
        }
        //		if (partialFlightKeys.nonIcaoAdes) {
        //			if (partialFlightKeys.nonIcaoAdes != fullFlightKeys.nonIcaoAdes) return false;
        //		}
        //		if (partialFlightKeys.nonIcaoAdep) {
        //			if (partialFlightKeys.nonIcaoAdep != fullFlightKeys.nonIcaoAdep) return false;
        //		}
        if (partialFlightKeys.airFiled) {
            if (partialFlightKeys.airFiled !== fullFlightKeys.airFiled) return false;
        }

        return true;
    }

    static getPoints(flight: FlightDto, model: FlightModelType): FlightPointDto[] {
        switch (model) {
            case FlightModelType.CTFM:
                return flight.ctfmPoints;
            case FlightModelType.RTFM:
                return flight.rtfmPoints;
            case FlightModelType.FTFM:
                return flight.ftfmPoints;
        }
        return null;
    }

    static getAirspacePoints(flight: FlightDto, model: FlightModelType): FlightAirspaceCrossing[] {
        switch (model) {
            case FlightModelType.CTFM:
                return flight.ctfmAirspacePoints;
            case FlightModelType.RTFM:
                return flight.rtfmAirspacePoints;
            case FlightModelType.FTFM:
                return flight.ftfmAirspacePoints;
        }
        return null;
    }

    static getFlightsWithIds<T extends FlightDto>(flights: T[], fids: FlightId[]): T[] {
        if (!flights || !fids) return null;
        const reply = [];
        for (const flight of flights) {
            if (fids.find(fid => FlightUtils.isSameFlight(flight.flightId, fid))) {
                reply.push(flight);
            }
        }
        return reply;
    }

    static findFlight<T extends FlightDto>(flights: T[], fid: FlightId): T {
        const idx = FlightUtils.findFlightIdx(flights, fid);
        return idx === -1 ? null : flights[idx];
    }

    static findFlightIdx<T extends FlightDto>(flights: T[], fid: FlightId): number {
        if (flights) {
            for (let i = 0; i < flights.length; i++) {
                if (FlightUtils.isSameFlight(flights[i].flightId, fid)) return i;
            }
        }
        return -1;
    }

    static getCfmuTypeString(flight: FlightDto): string {
        switch (flight.cfmuFlightType) {
            case CfmuFlightType.ACT:
                return 'A';
            case CfmuFlightType.IFPL:
                return 'I';
            case CfmuFlightType.PREDICTED_FLIGHT:
                return 'P';
            case CfmuFlightType.MFD:
                return 'M';
            case CfmuFlightType.RPL:
                return 'R';
            case CfmuFlightType.TERMINATED:
                return 'E';
            case CfmuFlightType.TACT_ACTIVATED:
                return 'T';
            // TODO: Finish implementation
            /*if (flight.actualTakeOffTime.after(CommonTimeUtils.nowHHMM()))
                return "t";
            else
                return "T";*/
        }
        return '?'; // should never happen
    }

    static toSuspensionShortString(status: SuspensionStatus): string {
        if (status == null) return null;
        switch (status) {
            case SuspensionStatus.AIRPORT_SUSPENSION:
                return 'AS';
            case SuspensionStatus.DELAY_CONFIRMATION:
                return 'DC';
            case SuspensionStatus.FLIGHT_PLAN_REVALIDATION:
                return 'RV';
            case SuspensionStatus.MANUAL_SUSPENSION:
                return 'MS';
            case SuspensionStatus.NOT_REPORTED_AS_AIRBORNE:
                return 'NR';
            case SuspensionStatus.NOT_SUSPENDED:
                return '';
            case SuspensionStatus.REGULATION_CONFIRMATION:
                return 'RC';
            case SuspensionStatus.SIT_TIME_OUT:
                return 'ST';
            case SuspensionStatus.SLOT_MISSED:
                return 'SM';
            case SuspensionStatus.TRAFFIC_VOLUMES_CONDITION:
                return 'TV'; //RVR
            case SuspensionStatus.V_MANUAL_SUSPENSION:
                return 'MS?'; // TODO : need to check with R23 NOP Spec what 2 char value is needed
        }
        return '??';
    }

    static getTrafficTypeString(type: TrafficType): string {
        switch (type) {
            case TrafficType.DEMAND:
                return 'E';
            case TrafficType.LOAD:
                return 'A';
            case TrafficType.REGULATED_DEMAND:
                return 'C';
        }
        return '?'; // IMPOSSIBLE
    }

    static getEntryTimeString(timeAtRefLoc: TimeAndTrafficType): string {
        if (timeAtRefLoc == null) return null;
        return TimeUtils.formatHHMM(timeAtRefLoc.time) + FlightUtils.getTrafficTypeString(timeAtRefLoc.type);
    }

    static getEtotCtotString(flight: FlightDto): string {
        if (flight.calculatedTakeOffTime) return TimeUtils.formatHHMM(flight.calculatedTakeOffTime) + 'C';
        else if (flight.estimatedTakeOffTime != null) return TimeUtils.formatHHMM(flight.estimatedTakeOffTime) + 'E';
        else return '---';
    }

    static compareEtotCtot(f1: FlightDto, f2: FlightDto): number {
        const d1 = f1.calculatedTakeOffTime ? f1.calculatedTakeOffTime : f1.estimatedTakeOffTime;
        const d2 = f2.calculatedTakeOffTime ? f2.calculatedTakeOffTime : f2.estimatedTakeOffTime;
        return TimeUtils.compareWithUndefined(d1, d2);
    }

    static getCdmTotForStatus(info: FlightCdmInfo): Date {
        let tot: Date = null;
        if (info != null) {
            switch (info.status) {
                case CdmStatus.ACTUAL_OFFBLOCK:
                    tot = info.atcTtot;
                    break;
                case CdmStatus.ESTIMATED:
                    tot = info.turnaroundTtot; // info.getEstimatedTtot();
                    break;
                case CdmStatus.PRE_SEQUENCED:
                    tot = Utils.firstNonNull(info.consolidatedTtot, info.earliestTtot); //info.getSequencedTtot();
                    break;
                case CdmStatus.TARGETED:
                    tot = info.turnaroundTtot; // info.getAoTtot();
                    break;
                case CdmStatus.DEPARTING_FROM_CDM_AIRPORT:
                    // do nothing?
                    break;
                case CdmStatus.DEPARTING_FROM_STANDARD_AIRPORT:
                    // do nothing
                    break;
                case CdmStatus.PREDICTED:
                    tot = Utils.firstNonNull(info.consolidatedTtot, info.earliestTtot, info.turnaroundTtot); // just take the highest value
                    break;
            }
        }
        return tot;
    }

    static getCdmStatusShortString(flight: FlightDto): string {
        let reply: string = '';
        switch (flight.cdmInfo.status) {
            case CdmStatus.ACTUAL_OFFBLOCK:
                reply = 'a';
                break;
            case CdmStatus.DEPARTING_FROM_CDM_AIRPORT:
                reply = '';
                break;
            case CdmStatus.DEPARTING_FROM_STANDARD_AIRPORT:
                reply = '';
                break;
            case CdmStatus.ESTIMATED:
                reply = 'e';
                break;
            case CdmStatus.PRE_SEQUENCED:
                reply = 's';
                break;
            case CdmStatus.TARGETED:
                reply = 't';
                break;
            case CdmStatus.PREDICTED:
                reply = 'p';
                break;
        }
        return reply;
    }

    static getFlightAtotTtotString(flight: FlightDto): string {
        let str: string = '';
        if (
            flight.cdmInfo == null ||
            Utils.isInList(
                flight.cdmInfo.status,
                CdmStatus.DEPARTING_FROM_CDM_AIRPORT,
                CdmStatus.DEPARTING_FROM_STANDARD_AIRPORT,
            )
        ) {
            if (flight.actualTakeOffTime != null) {
                str = TimeUtils.formatHHMM(flight.actualTakeOffTime);
            }
        } else if (Utils.isInList(flight.cfmuFlightType, CfmuFlightType.ACT, CfmuFlightType.TERMINATED)) {
            // = has taken off, so use ATOT
            if (flight.actualTakeOffTime != null) {
                str = TimeUtils.formatHHMM(flight.actualTakeOffTime) + 'a';
            }
        } else {
            const tot: Date = FlightUtils.getCdmTotForStatus(flight.cdmInfo);
            if (tot == null) {
                if (flight.actualTakeOffTime != null) {
                    str = TimeUtils.formatHHMM(flight.actualTakeOffTime);
                }
            } else {
                // in line with CHMI - show the time corresponding to the state, and not the most up to date TTOT value
                // (Such a difference can occur when in the NoSlotBefore state).
                str = TimeUtils.formatHHMM(tot) + FlightUtils.getCdmStatusShortString(flight);
            }
        }
        return str;
    }

    private static getFlightAtotTtot(flight: FlightDto): Date {
        if (
            flight.cdmInfo == null ||
            Utils.isInList(
                flight.cdmInfo.status,
                CdmStatus.DEPARTING_FROM_CDM_AIRPORT,
                CdmStatus.DEPARTING_FROM_STANDARD_AIRPORT,
            )
        ) {
            if (flight.actualTakeOffTime != null) {
                return flight.actualTakeOffTime;
            }
        } else if (Utils.isInList(flight.cfmuFlightType, CfmuFlightType.ACT, CfmuFlightType.TERMINATED)) {
            // = has taken off, so use ATOT
            if (flight.actualTakeOffTime != null) {
                return flight.actualTakeOffTime;
            }
        } else {
            const tot: Date = FlightUtils.getCdmTotForStatus(flight.cdmInfo);
            if (tot == null) {
                if (flight.actualTakeOffTime != null) {
                    return flight.actualTakeOffTime;
                }
            } else {
                return tot;
            }
        }
    }

    static compareFlightAtotTtot(f1: FlightDto, f2: FlightDto): number {
        const d1 = FlightUtils.getFlightAtotTtot(f1);
        const d2 = FlightUtils.getFlightAtotTtot(f2);
        return TimeUtils.compareWithUndefined(d1, d2);
    }

    static toCtotLimitChar(reason: CtotLimitReasonType): string {
        if (reason == null) return null;
        switch (reason) {
            case CtotLimitReasonType.FORCED_BY_CHAMAN:
                return 'c';
            case CtotLimitReasonType.FORCED_BY_NMOC:
                return 'f';
            case CtotLimitReasonType.FORCED_BY_STAM_MEASURE:
                return 's';
            case CtotLimitReasonType.FORCED_BY_TOWER:
                return 't';
            case CtotLimitReasonType.LIMITED_BY_VIOLATION:
                return 'V';
            case CtotLimitReasonType.LIMITED_BY_VIOLATION_THEN_ZERO_RATE_OR_RVR:
                return 'X';
            case CtotLimitReasonType.SLOT_EXTENSION:
                return ''; // could not find a code for this?
            case CtotLimitReasonType.SLOT_TIME_NOT_LIMITED:
                return '';
            case CtotLimitReasonType.WAS_FORCED_BY_NMOC:
                return 'w';
        }
        return '??';
    }

    static toDelayString(flight: FlightDto): string {
        let str: string = null;
        if (flight.calculatedTakeOffTime != null) {
            const delayInMin: number = flight.delayInMs / TimeUtils.MIN_TO_MS;
            //	boolean isi = ClientFlightUtils.isSlotIssued(flight);
            str = flight.slotIssued ? delayInMin.toString() : '*' + delayInMin + '*';
        }
        return str;
    }

    static exclusionString(dto: ExclusionFromRegulationsDto): string {
        if (dto == null) return null;
        const prefix: string = dto.onTrafficVolume === true ? '!' : '';
        if (dto.all === true) {
            return prefix + 'A'; // not specified in the CUA PORTAL SRD.. But don't know what to return.
        } else if (dto.count > 0) {
            return prefix + dto.count;
        } else if (dto.hasBeenExcluded) {
            return 'W';
        } else {
            return '';
        }
    }

    static getReadyShortString(flight: FlightDto): string {
        if (flight.readyStatus == null) return '';
        if (flight.readyStatus.readyToDepart === true) return 'R';
        if (flight.readyStatus.readyForImprovement === true) return 'I';
        return '';
        // in theory, there is a 3rd value "S" - implying SIP is needed.
    }

    static getBestTaxiTimeInMs(flight: FlightDto): number {
        if (flight.readyStatus?.readyToDepart && flight.readyStatus?.revisedTaxiTimeInMs >= 0)
            return flight.readyStatus.revisedTaxiTimeInMs;
        if (flight.currentDepartureTaxiTimeAndProcedure) {
            return flight.currentDepartureTaxiTimeAndProcedure.taxiTimeInMs;
        } else if (flight.cdmInfo != null && flight.cdmInfo.taxiTimeInMs >= 0) {
            return flight.cdmInfo.taxiTimeInMs;
        } else return flight.taxiTimeInMs;
    }

    static getBestTakeOffTime(flight: FlightDto): Date {
        return Utils.firstNonNull(
            flight?.actualTakeOffTime,
            flight?.calculatedTakeOffTime,
            flight?.estimatedTakeOffTime,
        );
    }

    static toTrendIndicator(trend: VerticalTrendType): string {
        if (trend == null) return null;
        let reply: string = null;
        switch (trend) {
            case VerticalTrendType.CLIMB:
                reply = '/';
                break;
            case VerticalTrendType.DESCENT:
                reply = '\\';
                break;
            case VerticalTrendType.CRUISE:
                reply = '=';
                break;
            case VerticalTrendType.NONE:
                reply = '';
                break;
        }
        return reply;
    }

    static toMPRWithHasOtherRegulationsString(flight: FlightDto): string {
        return flight.mostPenalisingRegulation
            ? `${flight.mostPenalisingRegulation}${flight.hasOtherRegulations ? '+' : ''}`
            : '';
    }

    static getArrivalTimeString(flight: FlightDto): string {
        if (flight.actualTimeOfArrival != null) {
            return TimeUtils.formatHHMM(flight.actualTimeOfArrival) + 'A';
        }
        if (flight.calculatedTimeOfArrival != null) {
            return TimeUtils.formatHHMM(flight.calculatedTimeOfArrival) + 'C';
        }
        if (flight.estimatedTimeOfArrival != null) {
            return TimeUtils.formatHHMM(flight.estimatedTimeOfArrival) + 'E';
        }
        return '---';
    }

    private static getArrivalTime(flight: FlightDto): Date {
        if (flight.actualTimeOfArrival != null) return flight.actualTimeOfArrival;
        if (flight.calculatedTimeOfArrival != null) return flight.calculatedTimeOfArrival;
        if (flight.estimatedTimeOfArrival != null) return flight.estimatedTimeOfArrival;
    }

    static compareArrivalTime(f1: FlightDto, f2: FlightDto): number {
        const d1 = FlightUtils.getArrivalTime(f1);
        const d2 = FlightUtils.getArrivalTime(f2);
        return TimeUtils.compare(d1, d2);
    }

    static getMinutesInRefLocation(flight: FlightDto) {
        // = debatable if we should add +1
        return TimeUtils.diffInMinutes(
            flight.timeAtReferenceLocationExit.time,
            flight.timeAtReferenceLocationEntry.time,
        );
    }

    static getLastValidityString(profileValidity: ProfileValidityDto): string {
        if (!profileValidity) return undefined;
        switch (profileValidity.profileValidityKind) {
            case ProfileValidityDto_ProfileValidityKind.NO_VIOLATIONS:
                return '+' + TimeUtils.formatHHMM(profileValidity.lastValidEobt);
            case ProfileValidityDto_ProfileValidityKind.VIOLATIONS:
                return '<' + TimeUtils.formatHHMM(profileValidity.lastValidEobt);
            case ProfileValidityDto_ProfileValidityKind.UNKNOWN:
                return '';
        }
        return '?';
    }

    static getDatasetShortString(flight: FlightDto) {
        if (!flight.dataset) return null;
        switch (flight.dataset.type) {
            case DatasetType.FORECAST:
                return 'F';
            case DatasetType.OPERATIONAL:
                return 'O';
            case DatasetType.SIMULATION:
                return 'S';
        }
        return '??';
    }

    static getDeltaTimeString(flight: FlightDto) {
        let str;
        const delta = flight.compareWithOtherTrafficType;
        if (delta?.deltaTimeInMs !== 0) {
            str = Utils.toSignedString(delta.deltaTimeInMs / TimeUtils.MIN_TO_MS);
        }
        return str;
    }

    static getDeltaLevelString(flight: FlightDto) {
        let str;
        const delta = flight.compareWithOtherTrafficType;
        if (delta?.deltaFlightLevel !== 0) {
            str = Utils.leftPadZero(delta.deltaFlightLevel, 3);
        }
        return str;
    }

    static getDeltaPosnString(flight: FlightDto) {
        let str;
        const delta = flight.compareWithOtherTrafficType;
        if (delta?.deltaPositionInNm !== 0) {
            str = delta.deltaPositionInNm.toString();
        }
        return str;
    }

    static getIntruderString(flight: FlightDto) {
        if (!flight.compareWithOtherTrafficType) return null;
        switch (flight.compareWithOtherTrafficType.intruderKind) {
            case IntruderKindType.HORIZONTAL_INTRUDER:
                return 'H';
            case IntruderKindType.MIXED_INTRUDER:
                return 'M';
            case IntruderKindType.NON_INTRUDER:
                return null;
            case IntruderKindType.VERTICAL_INTRUDER:
                return 'V';
        }
        return '?'; // should never happen
    }

    static getAllRegulations(flights: FlightDto[]): string[] {
        if (!flights) return null;
        const regulations = new Set<string>();
        for (const flight of flights) {
            if (flight.mostPenalisingRegulation) {
                regulations.add(flight.mostPenalisingRegulation);
            }
            if (flight.regulationLocations) {
                for (const regLoc of flight.regulationLocations) {
                    regulations.add(regLoc.regulationId);
                }
            }
        }
        return Array.from(regulations);
    }

    static hasAnyRegulation(flight: FlightDto, regulations: string[]): boolean {
        if (!regulations) return false;
        for (const regul of regulations) {
            if (FlightUtils.hasRegulation(flight, regul)) return true;
        }
        return false;
    }

    static hasRegulation(flight: FlightDto, regul: string): boolean {
        if (!regul) return false;
        if (regul === flight.mostPenalisingRegulation) return true;
        for (const regLoc of flight.regulationLocations) {
            if (regul === regLoc.regulationId) return true;
        }
        return false;
    }

    static toReroutingIndicatorShortString(indicator: ReroutingIndicator): string {
        if (indicator?.rerouted && indicator?.reason) {
            return this.toReroutingReasonShortString(indicator.reason);
        }
    }

    static toReroutingReasonShortString(reason: ReroutingReason): string {
        switch (reason) {
            case ReroutingReason.AO:
                return 'O';
            case ReroutingReason.ATC_PURPOSE_PROPOSAL:
                return 'A';
            case ReroutingReason.ATFCM_PURPOSE_PROPOSAL:
                return 'C';
            case ReroutingReason.ATFM_EXECUTED:
                return 'M';
            case ReroutingReason.CDR_OPPORTUNITY_PROPOSAL:
                return 'R';
            case ReroutingReason.FLIGHT_EFFICIENCY_PURPOSE_PROPOSAL:
                return 'F';
            case ReroutingReason.STAM_PURPOSE_PROPOSAL:
                return 'S';
        }
        return '?';
    }

    static toAowirShortString(reroutable: ReroutableStatusType) {
        if (reroutable) {
            switch (reroutable) {
                case ReroutableStatusType.CANNOT_BE_REROUTED:
                    return 'N';
                case ReroutableStatusType.TRY_ALLOWED:
                    return 'T';
                case ReroutableStatusType.TRY_AND_APPLY_ALLOWED:
                    return 'A';
            }
        }
        return '?';
    }

    static toRouteIdString(routeId: ReroutingRouteIdDto) {
        if (!routeId) return null;
        const std = routeId.standardRouteId;
        return std.adep + ' ' + std.ades + ' ' + FlightUtils.toRouteTypeChar(routeId.routeType) + std.seqNr;
    }

    static toRouteTypeChar(routeType: ReroutingRouteType) {
        switch (routeType) {
            case ReroutingRouteType.GENERATED:
                return 'G';
            case ReroutingRouteType.STANDARD:
                return '';
            case ReroutingRouteType.USER:
                return 'U';
            case ReroutingRouteType.VERTICAL:
                return 'V';
        }
        return '??'; // should never happen.
    }

    static toRouteTypeShortString(routeType: ReroutingRouteType) {
        switch (routeType) {
            case ReroutingRouteType.GENERATED:
                return 'GR';
            case ReroutingRouteType.STANDARD:
                return 'SR';
            case ReroutingRouteType.USER:
                return 'UR';
            case ReroutingRouteType.VERTICAL:
                return 'V*'; // added an asterisk for it to be more visible. Need to determine what NM displays in CHMI in R23.5
        }
        return '??'; // should never happen.
    }

    static toRegulationIdString(locations: Array<FlightRegLocation>, regulToIgnore?: string): string {
        if (!locations) return null;
        let reply: string = '';
        for (const rloc of locations) {
            reply += rloc.regulationId + ' ';
        }
        return reply;
    }

    static toProbabilityString(value: number): string {
        return value > 0.0 ? Math.round(value * 100) + '%' : null;
    }

    static toShortLoadString(flight: FlightDto): string {
        const state = flight?.worstLoadStateAtReferenceLocation;
        if (!state) return null;
        if (state.occupancy) {
            switch (state.occupancy) {
                case OtmvAlert_AlertStatus.PEAK:
                    return 'P';
                case OtmvAlert_AlertStatus.SUSTAINED:
                    return 'S';
            }
        } else if (state.entry) {
            switch (state.entry) {
                case LoadClassification.HIGH_THRESHOLD:
                    return 'H';
                case LoadClassification.LOW_THRESHOLD:
                    return 'L';
                case LoadClassification.NORMAL:
                    return null;
                case LoadClassification.OVERLOAD:
                    return 'O';
                case LoadClassification.UNDEFINED:
                    return null;
            }
        }
    }

    static getLateStatusString(flight: FlightDto): string {
        if (flight.lateFiler && flight.lateUpdater) return 'LFU';
        if (flight.lateFiler) return 'LF';
        if (flight.lateUpdater) return 'LU';
        return null;
    }

    static getWorstMcdmStateString(state: McdmState): string {
        if (state) {
            switch (state) {
                case McdmState.ABANDONED:
                    return null;
                case McdmState.COORDINATED:
                    return 'COORD';
                case McdmState.DRAFT:
                    return 'DRAFT';
                case McdmState.FINISHED:
                    return 'FINISHED';
                case McdmState.IMPLEMENTED:
                    return 'IMPLTED';
                case McdmState.IMPLEMENTING:
                    return 'FOR IMPL';
                case McdmState.INTERRUPTED:
                    return 'INTRUPT';
                case McdmState.PROPOSED:
                    return 'PROP';
            }
            return '???'; // should never happen
        }
    }

    static getMEAString(flight: FlightDto, mprIsDisplayed: boolean = true): string {
        if (!flight || !flight.atfcmMeasureLocations) return null;
        if (!mprIsDisplayed && flight.mostPenalisingRegulation) return flight.mostPenalisingRegulation;

        let reRoute: FlightAtfcmMeasureLoc = null;
        let mcdmOnly: FlightAtfcmMeasureLoc = null;
        for (const meaLoc of flight.atfcmMeasureLocations) {
            if (
                meaLoc.mcdmState !== McdmState.ABANDONED &&
                (!mprIsDisplayed || meaLoc.measureId.id !== flight.mostPenalisingRegulation)
            ) {
                // there is a 3rd condition : the flight must be in cherry picking list.. don't know how to test for that
                switch (meaLoc.clazz) {
                    case FLIGHT_ATFCM_REGULATION_LOC_CLAZZ_VALUE:
                        return meaLoc.measureId.id;
                    case FLIGHT_ATFCM_REROUTING_LOC_CLAZZ_VALUE:
                        if (!reRoute) reRoute = meaLoc;
                        break;
                    case FLIGHT_ATFCM_MCDM_ONLY_LOC_CLAZZ_VALUE:
                        if (!mcdmOnly) mcdmOnly = meaLoc;
                        break;
                }
            }
        }
        return reRoute ? reRoute.measureId.id : mcdmOnly ? mcdmOnly.measureId.id : null;
    }

    static getMKString(flight: FlightDto): string {
        if (flight?.mcdmInfo) {
            const info = flight.mcdmInfo;

            const delayChar =
                info.nrAssociatedMcdmRegulations === 0 ? ' ' : info.nrAssociatedMcdmRegulations === 1 ? 'd' : 'D';
            const reRouteChar =
                info.nrAssociatedMcdmReroutings === 0 ? ' ' : info.nrAssociatedMcdmReroutings === 1 ? 'r' : 'R';
            const mcdmChar =
                info.nrAssociatedMcdmOnlyMeaures === 0 ? ' ' : info.nrAssociatedMcdmOnlyMeaures === 1 ? 'm' : 'M';
            return delayChar + reRouteChar + mcdmChar;
        }
    }

    static getMTTString(flight: FlightDto): string {
        let value = '';
        if (flight?.arrivalInformation?.minimumTurnaroundTimeInMs > 0) {
            value = flight.arrivalInformation.minimumTurnaroundTimeInMs / TimeUtils.MIN_TO_MS + 'a';
        } else if (Utils.isExists(flight?.minimumTurnaroundTimeInMin)) {
            value = flight.minimumTurnaroundTimeInMin.toString();
        }
        return value;
    }

    static getTtaAtoString(dto: TargetTimeDto): string {
        if (dto?.actualTimeAtTarget) {
            const diffInMin = TimeUtils.diffInMinutesUpper(
                dto.actualTimeAtTarget.estimatedActualTimeAtTarget,
                dto.targetTime,
            );
            return TimeUtils.formatHHMM(dto.actualTimeAtTarget.estimatedActualTimeAtTarget) + ' (' + diffInMin + ')';
        }
    }

    static getRoutePointId(routePoint: RoutePoint): string {
        if (routePoint.navPoint) {
            return routePoint.navPoint.id;
        } else if (routePoint.aerodrome) {
            return routePoint.aerodrome.icaoId; // there is only one ID for aerodromes. So return it.
        }
        return null;
    }

    static getRoutePointCoordinates(routePoint: RoutePoint): LatLonPoint {
        if (routePoint.navPoint) {
            return routePoint.navPoint;
        } else if (routePoint.aerodrome) {
            return routePoint.aerodrome.point;
        }
        return null;
    }

    private static defaultFlightFieldPropertyName(field: FlightFieldType): string {
        const enumName = field[1];
        return Utils.fromUpperCaseToCamelCase(enumName);
    }

    private static toStandardFlightFieldPropertyName(field: StandardFlightFieldType): keyof FlightDto {
        const enumName = field[1];
        switch (enumName) {
            case 'AIRCRAFT_ADDRESS':
                return 'aircraftAddress';
            case 'AC_TYPE':
                return 'aircraftType';
            case 'AO':
                return 'aircraftOperator';
            case 'AOBT':
                return 'actualOffBlockTime';
            case 'ALTERNATE_AERODROMES':
                return 'alternateAerodromes';
            case 'API_SUBMISSION_RULES':
                return 'apiSubmissionRules';
            case 'APPLICABLE_SCENARIOS':
                return 'applicableScenarios';
            case 'ARRIVAL_INFORMATION':
                return 'arrivalInformation';
            case 'ATA':
                return 'actualTimeOfArrival';
            case 'ATFCM_MEASURE_LOCATIONS':
                return 'atfcmMeasureLocations';
            case 'ATOT':
                return 'actualTakeOffTime';
            case 'AVOIDED_REGULATIONS':
                return 'avoidedRegulationIds';
            case 'CAUGHT_IN_HOTSPOTS':
                return 'caughtInHotspots';
            case 'CDM_EOBT':
                return 'cdmEobt';
            case 'CDM_INFO':
                return 'cdmInfo';
            case 'CFMU_FLIGHT_TYPE':
                return 'cfmuFlightType';
            case 'COBT':
                return 'calculatedOffBlockTime';
            case 'COMPARE_WITH_OTHER_TRAFFIC_TYPE':
                return 'compareWithOtherTrafficType';
            case 'CONFIRMED_CPR':
                return 'confirmedCprDistance';
            case 'CTA':
                return 'calculatedTimeOfArrival';
            case 'CTFM_AIRSPACE_PROFILE':
                return 'ctfmAirspacePoints';
            case 'CTFM_POINT_PROFILE':
                return 'ctfmPoints';
            case 'CTFM_REQUESTED_FLIGHT_LEVELS':
                return 'ctfmRequestedFlightLevels';
            case 'CTOT':
                return 'calculatedTakeOffTime';
            case 'CTOT_LIMIT_REASON':
                return 'ctotLimitReason';
            case 'CURRENT_DEPARTURE_TAXI_TIME_AND_PROCEDURE':
                return 'currentDepartureTaxiTimeAndProcedure';
            case 'DATA_VERSION_NUMBER':
                return 'dataVersionNumber';
            case 'DELAY':
                return 'delayInMs';
            case 'DEPARTURE_TOLERANCE':
                return 'departureTolerance';
            case 'DIVERTED_ADES':
                return 'divertedAdes';
            case 'EET':
                return 'estimatedElapsedTimeInMs';
            case 'ETA':
                return 'estimatedTimeOfArrival';
            case 'ETOT':
                return 'estimatedTakeOffTime';
            case 'EXCLUDED_REGULATIONS':
                return 'excludedRegulationIds';
            case 'EXCLUSION_FROM_REGULATIONS':
                return 'exclusionFromRegulations';
            case 'EXEMPTED_FROM_REGULATIONS':
                return 'exemptedFromRegulations';
            case 'FILED_REGISTRATION':
                return 'filedRegistration';
            case 'FL_AT_REFLOC_ENTRY':
                return 'flightLevelAtReferenceLocationEntry';
            case 'FL_AT_REFLOC_EXIT':
                return 'flightLevelAtReferenceLocationExit';
            case 'FLIGHT_HISTORY':
                return 'flightHistory';
            case 'FLIGHT_STATE':
                return 'flightState';
            case 'FTFM_AIRSPACE_PROFILE':
                return 'ftfmAirspacePoints';
            case 'FTFM_POINT_PROFILE':
                return 'ftfmPoints';
            case 'FTFM_REQUESTED_FLIGHT_LEVELS':
                return 'ftfmRequestedFlightLevels';
            case 'FUEL_CONSUMPTION':
                return 'fuelConsumptionInKg';
            case 'HAS_BEEN_FORCED':
                return 'hasBeenForced';
            case 'HAS_OTHER_REGULATIONS':
                return 'hasOtherRegulations';
            case 'HIGHEST_AIRSPACE_PROFILE':
                return undefined;
            case 'HIGHEST_POINT_PROFILE':
                return undefined;
            case 'HOTSPOTS':
                return 'hotspots';
            case 'ICAO_ROUTE':
                return 'icaoRoute';
            case 'INITIAL_RFL':
                return 'initialRfl';
            case 'PROPOSAL_CONTEXT':
                return 'proposalContext';
            case 'LAST_ATFM_MESSAGE_TYPE':
                return 'lastAtfmMessageType';
            case 'LATE_FILER':
                return 'lateFiler';
            case 'LAST_KNOWN_POSITION':
                return 'lastKnownPosition';
            case 'LATE_UPDATER':
                return 'lateUpdater';
            case 'MCDM_INFO':
                return 'mcdmInfo';
            case 'MPR':
                return 'mostPenalisingRegulation';
            case 'MPR_CAUSE':
                return 'mostPenalizingRegulationCause';
            case 'OPERATING_AO':
                return 'operatingAircraftOperator';
            case 'OPLOG':
                return 'opLogHistory';
            case 'PROFILE_VALIDITY':
                return 'profileValidity';
            case 'PROPOSAL_INFORMATION':
                return 'proposalInformation';
            case 'READY_EOBT':
                return 'readyEobt';
            case 'READY_STATUS':
                return 'readyStatus';
            case 'REGULATION_LOCATIONS':
                return 'regulationLocations';
            case 'REQUESTED_INITIAL_SPEED':
                return 'requestedInitialSpeed';
            case 'REROUTABLE':
                return 'reroutable';
            case 'BEST_REROUTING_INDICATOR':
                return 'bestReroutingIndicator';
            case 'REVISION_TIMES':
                return 'revisionTimes';
            case 'RFL':
                return 'rfl';
            case 'ROUTE_CHARGES':
                return 'routeChargesInEuros';
            case 'ROUTE_LENGTH':
                return 'routeLength';
            case 'RRT_INDICATOR':
                return 'reroutingIndicator';
            case 'RTFM_AIRSPACE_PROFILE':
                return 'rtfmAirspacePoints';
            case 'RTFM_POINT_PROFILE':
                return 'rtfmPoints';
            case 'RTFM_REQUESTED_FLIGHT_LEVELS':
                return 'rtfmRequestedFlightLevels';
            case 'RUNWAY_VISUAL_RANGE':
                return 'runwayVisualRange';
            case 'SLOT_ISSUED':
                return 'slotIssued';
            case 'SLOT_SWAP_CANDIDATE_LIST':
                return 'slotSwapCandidateList';
            case 'SLOT_SWAP_COUNTER':
                return 'slotSwapCounter';
            case 'SLOT_ZONE':
                return 'slotZone';
            case 'SUSPENSION':
                return 'suspensionStatus';
            case 'TARGET_TIME_OVER_FIX':
                return 'targetTimeOverFix';
            case 'TAXI_TIME':
                return 'taxiTimeInMs';
            case 'TIME_AT_REFLOC_ENTRY':
                return 'timeAtReferenceLocationEntry';
            case 'TIME_AT_REFLOC_EXIT':
                return 'timeAtReferenceLocationExit';
            case 'TREND_ENTRY':
                return 'trendAtReferenceLocationEntry';
            case 'TREND_EXIT':
                return 'trendAtReferenceLocationExit';
            case 'TREND_MIDDLE':
                return 'trendAtReferenceLocationMiddle';
            case 'WAKE_TURBULENCE_CATEGORY':
                return 'wakeTurbulenceCategory';
            case 'WORST_LOAD_STATE_AT_REFLOC':
                return 'worstLoadStateAtReferenceLocation';
        }
        throw 'Unknown field ' + field;
    }

    private static toStandardNppFlightFieldPropertyName(field: StandardNppFlightFieldType): keyof FlightDto {
        const enumName = field[1];
        switch (enumName) {
            case 'PROBABILITY_IN_COUNT_STEPS':
                return 'probabilityInCountSteps';
            case 'PROBABILITY_IN_QUERY':
                return 'probabilityInQuery';
            case 'MINIMUM_TURNAROUND_TIME':
                return 'minimumTurnaroundTimeInMin';
            case 'TURNAROUND_IMPACT_MARGIN':
                return 'turnaroundImpactMarginInMin';
        }
        throw 'Unknown field ' + field;
    }

    private static toInnoveFlightFieldPropertyName(field: InnoveFlightFieldType): keyof FlightDto {
        const enumName = field[1];
        switch (enumName) {
            case 'REGULATION_RISK_IN_BLOCK_TIME_ZONE':
                return 'regulationRiskInBlockTimeZone';
            case 'REGULATION_RISK_OFF_BLOCK_TIME_ZONE':
                return 'regulationRiskOffBlockTimeZone';
            case 'REGULATION_RISK_TAKEOFF_TIME_ZONE':
                return 'regulationRiskTakeOffTimeZone';
            case 'PRIORITY':
                return 'priority';
            case 'CTFM_DYNAMIC_VOLUME_PROFILE':
                return 'ctfmDynamicVolumeProfile';
            case 'FTFM_DYNAMIC_VOLUME_PROFILE':
                return 'ftfmDynamicVolumeProfile';
            case 'HIGHEST_DYNAMIC_VOLUME_PROFILE':
                return null;
            case 'RTFM_DYNAMIC_VOLUME_PROFILE':
                return 'rtfmDynamicVolumeProfile';
        }
        throw 'Unknown field ' + field;
    }

    static removeDuplicates(flights: Array<FlightDto>) {
        return flights.filter(
            (flight, index, self) =>
                index ===
                self.findIndex(otherFlight => FlightUtils.isSameFlight(flight?.flightId, otherFlight?.flightId)),
        );
    }

    /**
     * Generates an enriched flight id based on the flight values.
     * @param flight
     */
    static getEnrichedFlightId(flight: FlightDto) {
        const enriched: EnrichedFlightId = {
            flightId: flight.flightId,
            dataset: flight.dataset,
        };
        //only add the proposal context field if it really exists, otherwise the comparison will fail (as having an undefined field is different of not having it)
        if (flight.proposalContext) {
            enriched.proposalContext = flight.proposalContext;
        }
        return enriched;
    }

    static isSameEnrichedFlight(fid1: EnrichedFlightId, fid2: EnrichedFlightId): boolean {
        if (fid1 === fid2) return true;
        if (!fid1 || !fid2) return false;
        const sameFlightId = FlightUtils.isSameFlight(fid1.flightId, fid2.flightId);
        if (!sameFlightId) return false;

        return Utils.isEquals(fid1.dataset, fid2.dataset) && Utils.isEquals(fid1.proposalContext, fid2.proposalContext);
    }

    static getFlights(flights: Array<FlightDto>, flightsId: Array<FlightId>) {
        const fidFilters = flightsId.map(KeyUtils.FLIGHT_ID_KEY_PROVIDER.getKey);
        return flights.filter(flight => fidFilters.includes(KeyUtils.FLIGHT_ID_KEY_PROVIDER.getKey(flight.flightId)));
    }

    static hasApplicableScenarios(flight: FlightDto) {
        return Utils.isNotEmpty(flight?.applicableScenarios);
    }

    static isNotDeparted(flight: FlightDto): boolean {
        const cfmuFlightType = flight.cfmuFlightType;
        return !(
            cfmuFlightType === CfmuFlightType.ACT ||
            cfmuFlightType === CfmuFlightType.TACT_ACTIVATED ||
            cfmuFlightType === CfmuFlightType.TERMINATED
        );
    }

    static toFlightCriticalityString(dto: FlightCriticalityIndicatorDto): string {
        if (dto) {
            switch (dto.kind) {
                case FlightCriticalityKind.CRITICAL_DUE_TO_AIRFRAME_UTILISATION:
                    return 'AIRF';
                case FlightCriticalityKind.CRITICAL_DUE_TO_AIRPORT_CLOSURE:
                    return 'CURF';
                case FlightCriticalityKind.CRITICAL_DUE_TO_CREW_TIME:
                    return 'CREW';
                case FlightCriticalityKind.CRITICAL_DUE_TO_NOISE_ABATEMENT:
                    return 'NOIS';
                case FlightCriticalityKind.CRITICAL_DUE_TO_OTHER_REASONS:
                    return 'OTHR';
                case FlightCriticalityKind.CRITICAL_DUE_TO_PASSENGER_CONNECTIONS:
                    return 'PAXC';
                case FlightCriticalityKind.CRITICAL_DUE_TO_PASSENGER_DELAY_COMPENSATION:
                    return 'PAXD';
                case FlightCriticalityKind.CRITICAL_DUE_TO_TURNAROUND_CRITICAL:
                    return 'NEXT';
                case FlightCriticalityKind.WAS_CRITICAL:
                    return 'CNCL';
            }
        }
    }

    static getConvectionImpact(impactLevel: number) {
        switch (impactLevel) {
            case 1:
                return 'LOW';
            case 2:
                return 'MEDIUM';
            case 3:
                return 'HIGH';
            case 4:
                return AppContext.getConvectionSource() == ConvectionSource.DWD ? 'VERY_HIGH' : 'CRITICAL';
            case 5:
                return 'CRITICAL';
            default:
                return '';
        }
    }
}
